import { Select } from '../../components/ui/Input'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { formatMoney, formatDate } from '../../lib/utils'
import { mudancaFechamentoDoCartao, mudancasDoCartao } from '../../lib/importacaoPdf'
import type { CreditCard, Fatura, FaturaDestinoPdf, FaturaPdfLida, ItemPreVisualizacaoCsv, SugestaoCompraNoFechamento } from '../../types'

const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const SITUACAO_INFO: Record<ItemPreVisualizacaoCsv['situacao'], { rotulo: string; variant: 'income' | 'info' | 'warning' }> = {
  existe: { rotulo: 'Já existe', variant: 'income' },
  nova: { rotulo: 'Nova', variant: 'info' },
  ajuste: { rotulo: 'Ajuste (crédito)', variant: 'warning' },
}

const rotuloDestino = (d: FaturaDestinoPdf) =>
  `Fatura de ${NOMES_MES[d.mes - 1].slice(0, 3).toLowerCase()}/${d.ano}, vence ${formatDate(d.vencimento).slice(0, 5)}`

interface ResumoProps {
  fatura: FaturaPdfLida
  destino: FaturaDestinoPdf
  faturaId: string
  faturas: Fatura[]
  onDestino: (faturaId: string) => void
  soma: number
  diferenca: number
  confere: boolean
  aceite: boolean
  onAceite: (v: boolean) => void
  cartao?: CreditCard
  atualizarCartao: boolean
  onAtualizarCartao: (v: boolean) => void
  /** Sugestão do extrato sobre a compra do dia do fechamento (a caixa só aparece quando difere do cartão). */
  sugestaoFechamento?: SugestaoCompraNoFechamento | null
  atualizarFechamento?: boolean
  onAtualizarFechamento?: (v: boolean) => void
}

/** Cabeçalho da prévia do PDF: emissor, datas, trava da soma, fatura de destino e sugestão de atualizar o cartão. */
export function ResumoPdf({ fatura, destino, faturaId, faturas, onDestino, soma, diferenca, confere, aceite, onAceite, cartao, atualizarCartao, onAtualizarCartao, sugestaoFechamento, atualizarFechamento = false, onAtualizarFechamento }: ResumoProps) {
  const padrao = destino.faturaId ?? ''
  const sugestao = mudancasDoCartao(fatura, cartao)
  const fechamento = mudancaFechamentoDoCartao(sugestaoFechamento, cartao)
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-line p-3 text-13">
        <p className="font-medium text-white">
          {fatura.emissor}{fatura.finalCartao ? ` · PDF do cartão final ${fatura.finalCartao}` : ''}
        </p>
        <p className="mt-1 text-muted">
          Vencimento {formatDate(fatura.vencimento)} · Fechamento {formatDate(fatura.fechamento)}
          {fatura.periodoInicio && fatura.periodoFim ? ` · Período ${formatDate(fatura.periodoInicio)} a ${formatDate(fatura.periodoFim)}` : ''}
        </p>
        <dl className="mt-2 grid grid-cols-3 gap-2">
          <div><dt className="text-2xs text-muted">Total da fatura</dt><dd className="font-semibold text-white">{formatMoney(fatura.totalFatura)}</dd></div>
          <div><dt className="text-2xs text-muted">Soma das linhas</dt><dd className="font-semibold text-white">{formatMoney(soma)}</dd></div>
          <div><dt className="text-2xs text-muted">Diferença</dt><dd className={`font-semibold ${confere ? 'text-income' : 'text-expense'}`}>{formatMoney(diferenca)}</dd></div>
        </dl>
        {fatura.avisos.map((a) => <p key={a} className="mt-1 text-12 text-warning">{a}</p>)}
      </div>

      {!confere && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 p-3 text-13">
          <p className="text-warning">
            A soma das linhas não bate com o total da fatura (diferença de {formatMoney(diferenca)}). Pode haver linha não lida ou valor lido errado: confira e corrija abaixo, ou importe assim mesmo.
          </p>
          <label className="mt-2 flex items-center gap-2 text-white">
            <input type="checkbox" checked={aceite} onChange={(e) => onAceite(e.target.checked)} />
            Entendo a diferença
          </label>
        </div>
      )}

      <Select label="Fatura de destino" value={faturaId === padrao ? 'padrao' : faturaId} onChange={(e) => onDestino(e.target.value === 'padrao' ? padrao : e.target.value)}>
        <option value="padrao">{rotuloDestino(destino)}{destino.existe ? '' : ' (será criada)'}{destino.paga ? ' (já paga)' : ''}</option>
        {faturas.filter((f) => f.id !== padrao).map((f) => <option key={f.id} value={f.id}>{NOMES_MES[f.mes - 1]} de {f.ano}</option>)}
      </Select>

      {sugestao && (
        <label className="flex items-start gap-2 rounded-lg border border-line p-3 text-13 text-white">
          <input type="checkbox" className="mt-0.5" checked={atualizarCartao} onChange={(e) => onAtualizarCartao(e.target.checked)} />
          <span>
            O PDF indica fecha dia {sugestao.diaFechamento}, vence dia {sugestao.diaVencimento}
            {sugestao.limite != null ? `, limite ${formatMoney(sugestao.limite)}` : ''}. Atualizar o cartão?
          </span>
        </label>
      )}

      {fechamento && onAtualizarFechamento && (
        <label className="flex items-start gap-2 rounded-lg border border-line p-3 text-13 text-white">
          <input type="checkbox" className="mt-0.5" checked={atualizarFechamento} onChange={(e) => onAtualizarFechamento(e.target.checked)} />
          <span>
            O extrato indica que a compra do dia do fechamento fica na fatura {fechamento.valor ? 'que fecha' : 'seguinte'}. Atualizar o cartão?
            {fechamento.motivo && <span className="mt-0.5 block text-12 text-muted">{fechamento.motivo}</span>}
            <span className="mt-0.5 block text-12 text-muted">As compras já lançadas não são movidas: só as próximas seguem a nova regra.</span>
          </span>
        </label>
      )}
    </div>
  )
}

export type EdicaoLinha = Partial<Pick<ItemPreVisualizacaoCsv, 'descricao' | 'valor' | 'data' | 'parcelaNumero' | 'parcelaTotalNumero'>>

const inteiroOuNada = (t: string) => (t === '' ? undefined : Math.max(1, Math.floor(Number(t))))

/** Linhas do PDF editáveis (data, descrição, valor, parcela) e desmarcáveis. */
export function TabelaPdf({ itens, marcados, onMarcar, onEditar, extra, travada }: {
  itens: ItemPreVisualizacaoCsv[]
  marcados: Set<number>
  onMarcar: (indice: number, marcado: boolean) => void
  onEditar: (indice: number, edicao: EdicaoLinha) => void
  /** Conteúdo extra por linha (mover, saldo transferido). */
  extra?: (item: ItemPreVisualizacaoCsv) => React.ReactNode
  /** Linha cuja marcação é decidida em outro lugar (par de possível duplicada). */
  travada?: (item: ItemPreVisualizacaoCsv) => boolean
}) {
  return (
    <div className="max-h-96 overflow-auto">
      <Table>
        <THead>
          <tr><Th></Th><Th>Data</Th><Th>Descrição</Th><Th>Parcela</Th><Th className="text-right">Valor</Th><Th>Situação</Th></tr>
        </THead>
        <tbody>
          {itens.map((item) => (
            <Tr key={item.indice}>
              <Td><input type="checkbox" aria-label="Importar esta linha" checked={marcados.has(item.indice)} disabled={item.pagamentoFaturaAnterior || travada?.(item)} onChange={(e) => onMarcar(item.indice, e.target.checked)} /></Td>
              <Td>
                <input
                  type="date" aria-label="Data" className="form-control w-36" value={item.data.slice(0, 10)}
                  onChange={(e) => e.target.value && onEditar(item.indice, { data: `${e.target.value}T00:00:00Z` })}
                />
              </Td>
              <Td className="min-w-48">
                <input aria-label="Descrição" className="form-control w-full" value={item.descricao} onChange={(e) => onEditar(item.indice, { descricao: e.target.value })} />
                {item.compraAntiga && <Badge variant="warning" className="mt-1 normal-case">Compra antiga, parcela não informada</Badge>}
                {!item.compraAntiga && item.aviso && <p className="mt-1 text-2xs text-warning">{item.aviso}</p>}
                {item.situacao === 'existe' && <p className="text-2xs text-muted">bate com "{item.transacaoExistenteDescricao}"</p>}
                {extra?.(item)}
              </Td>
              <Td className="whitespace-nowrap">
                <span className="inline-flex items-center gap-1 text-muted">
                  <input
                    type="number" min={1} aria-label="Parcela atual" className="form-control w-14 text-center" value={item.parcelaNumero ?? ''}
                    onChange={(e) => onEditar(item.indice, { parcelaNumero: inteiroOuNada(e.target.value) })}
                  />
                  de
                  <input
                    type="number" min={1} aria-label="Total de parcelas" className="form-control w-14 text-center" value={item.parcelaTotalNumero ?? ''}
                    onChange={(e) => onEditar(item.indice, { parcelaTotalNumero: inteiroOuNada(e.target.value) })}
                  />
                </span>
              </Td>
              <Td>
                <input
                  type="number" step="0.01" aria-label="Valor" className="form-control w-28 text-right" value={item.valor}
                  onChange={(e) => e.target.value !== '' && onEditar(item.indice, { valor: Number(e.target.value) })}
                />
              </Td>
              <Td>
                <Badge variant={SITUACAO_INFO[item.situacao].variant}>{SITUACAO_INFO[item.situacao].rotulo}</Badge>
                {item.pagamentoFaturaAnterior && <p className="mt-1 text-2xs text-muted">Pagamento de fatura: não é importado (já está na conta)</p>}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
